import {
  useEffect,
  useRef,
} from "react";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import {
  Router,
} from "react-router";
import {
  tokenStore,
} from "CommonCore/apifetch";
import {
  inline,
} from "CommonState/action-helpers";


/**
 * This action type will be dispatched when your history
 * receives a location change.
 */
const ROUTER_ON_LOCATION_CHANGED = "@@router/ON_LOCATION_CHANGED";
const onLocationChanged = (location, action) => ({
  type: ROUTER_ON_LOCATION_CHANGED,
  payload: { location, action },
});

function matchLocationChangeAction(action) {
  // TODO NIKITA
  const ROUTER_ON_LOCATION_CHANGED = "@@router/ON_LOCATION_CHANGED";

  return action.type === ROUTER_ON_LOCATION_CHANGED;
}

/**
 * This action type will be dispatched by the history actions below.
 * If you're writing a middleware to watch for navigation events, be sure to
 * look for actions of this type.
 */
const ROUTER_CALL_HISTORY_METHOD = "@@router/CALL_HISTORY_METHOD";
function updateLocation(method, asEffect = true) {
  return (...args) => ({
    type: ROUTER_CALL_HISTORY_METHOD,
    payload: { method, args, asEffect },
  });
}

function matchUpdateLocationActions(action) {
  return action.type === ROUTER_CALL_HISTORY_METHOD;
}

/**
 * Pushes a new location onto the history stack, increasing its length by one.
 * If there were any entries in the stack after the current one, they are
 * lost.
 *
 * @param to - The new URL
 * @param state - Data to associate with the new location
 */
const push = updateLocation("push");
const pushStraight = updateLocation("push", false);

/**
 * Replaces the current location in the history stack with a new one.  The
 * location that was replaced will no longer be available.
 *
 * @param to - The new URL
 * @param state - Data to associate with the new location
 */
const replace = updateLocation("replace");
const replaceStraight = updateLocation("replace", false);

/**
 * Navigates to the next entry in the stack. Identical to go(1).
 */
const go = updateLocation("go");
const goStraight = updateLocation("go", false);

/**
 * Goes back one entry in the history stack. Identical to go(-1).
 */
const back = updateLocation("back");
const backStraight = updateLocation("back", false);

/**
 * Navigates to the next entry in the stack. Identical to go(1).
 */
const forward = updateLocation("forward");
const forwardStraight = updateLocation("forward", false);
const routerActions = {
  push,
  replace,
  go,
  back,
  forward,
};

// Middleware
function createRouterMiddleware(history) {
  return () => next => action => {
    if (!matchUpdateLocationActions(action)) {
      return next(action);
    }
    const callHistoryMethod = () => {
      /*
       * Typescript is not able to narrow the arguments types correctly, so we need to handle
       * each argument constellation seperately
       */
      switch (action.payload.method) {
        case "back":
        case "forward":
          history[action.payload.method]();
          break;
        case "go":
          history[action.payload.method](...action.payload.args);
          break;
        case "push":
          history[action.payload.method](...action.payload.args);
          break;
        case "replace":
          history[action.payload.method](...action.payload.args);
          break;
      }
    };
    if (action.payload.asEffect === true) {
      queueMicrotask(callHistoryMethod);
      return;
    }
    callHistoryMethod();
  };
}
// Reducer
const ROUTER_REDUCER_MAP_KEY = "router";

function createRouterReducer(history) {
  const initialRouterState = {
    location: history.location,
    action: history.action,
  };

  /*
   * This reducer will update the state with the most recent location history
   * has transitioned to.
   */
  return (state = initialRouterState, action) => {
    if (matchLocationChangeAction(action)) {
      return action.payload;
    }

    return state;
  };
}

function createRouterReducerMapObject(history) {
  return {
    [ROUTER_REDUCER_MAP_KEY]: createRouterReducer(history),
  };
}

function reduxRouterSelector(state) {
  return state[ROUTER_REDUCER_MAP_KEY];
}

function ReduxRouter(_a) {
  const { routerSelector = reduxRouterSelector } = _a;
  const props = { ..._a };
  delete props.routerSelector;
  const dispatch = useDispatch();
  const skipHistoryChange = useRef();
  const state = useSelector(routerSelector);

  useEffect(() => {
    return props.history.listen(nextState => {
      if (skipHistoryChange.current === true) {
        skipHistoryChange.current = false;
        return;
      }
      dispatch(onLocationChanged(nextState.location, nextState.action));
      // TODO NIKITA ROUTING
      dispatch(inline("pathname", state => {
        state.pathname = nextState.location.pathname;
        const newState = nextState.location.pathname;
        tokenStore._set("pathname", newState.toString());
      }));
    });
  }, [props.history, dispatch]);

  useEffect(() => {
    if (props.history.location !== state.location) {
      dispatch(onLocationChanged(props.history.location, props.history.action));
    }
  }, []);

  useEffect(() => {
    if (skipHistoryChange.current === undefined) {
      skipHistoryChange.current = false;
    } else if (props.history.location !== state.location) {
      skipHistoryChange.current = true;
      props.history.replace(state.location);
    }
  }, [state.location, props.history]);

  return (
    <Router
      navigationType={state.action}
      location={state.location}
      basename={props.basename}
      navigator={props.history}
    >
      {props.children}
    </Router>
  );
}

export {
  ROUTER_CALL_HISTORY_METHOD,
  ROUTER_ON_LOCATION_CHANGED,
  ROUTER_REDUCER_MAP_KEY,
  ReduxRouter,
  back,
  backStraight,
  createRouterMiddleware,
  createRouterReducer,
  createRouterReducerMapObject,
  forward,
  forwardStraight,
  go,
  goStraight,
  matchLocationChangeAction,
  matchUpdateLocationActions,
  onLocationChanged,
  push,
  pushStraight,
  reduxRouterSelector,
  replace,
  replaceStraight,
  routerActions,
};
